import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layouts/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * /calendar — EventON-style festival calendar (v2).
 *
 * Design cues taken from the EventON WordPress plugin (Manuel's reference):
 *   - Month "jump" pills + prev/next navigation.
 *   - Day rows with a date chip (big day number + weekday) and stacked
 *     event bars, each with a category color edge.
 *   - Click an event → expands in place (lightbox-style accordion) with
 *     image, full time range, venue, description, spots left.
 *   - Locked events (no pass) show the teaser + a padlock and an unlock CTA;
 *     the server strips premium fields so this is enforcement, not styling.
 *
 * PREVIEW BANNER: current offers are sample data while venue onboarding
 * completes. Flip SHOW_PREVIEW_BANNER to false when the real schedule lands.
 */

const SHOW_PREVIEW_BANNER = true;

export const Route = createFileRoute("/calendar")({
  component: CalendarPage,
});

/* ---------------------------------------------------------------- types */

interface Offer {
  id: string;
  title: string;
  category: string | null;
  teaser: string | null;
  image_url: string | null;
  starts_at: string | null;
  ends_at: string | null;
  all_day: boolean;
  is_locked: boolean;
  spots_remaining: number | null;
  venue: { id: string | null; name: string | null; location_hint: string | null; profile?: Record<string, unknown> } | null;
  description?: string | null;
}
interface Day { date: string; label: string | null; offers: Offer[]; }
interface CalendarResp {
  range: { from: string; to: string; tz: string };
  user: { authenticated: boolean; has_pass: boolean };
  days: Day[];
}

/* ------------------------------------------------------------ category ui */

const CAT: Record<string, { label: string; color: string }> = {
  fnb:         { label: "Food & Drink", color: "#d4772a" },   // marigold
  wellness:    { label: "Wellness",     color: "#1d6b5e" },   // teal accent
  music:       { label: "Music",        color: "#b23226" },   // folk red
  art:         { label: "Art",          color: "#c08a2e" },   // gold
  hospitality: { label: "Hospitality",  color: "#cd5b3a" },   // terracota (coral)
  tour:        { label: "Tour",         color: "#11302a" },   // verde palma
  experience:  { label: "Experience",   color: "#9c6e1f" },   // oro oscuro
  workshop:    { label: "Workshop",     color: "#6b4423" },   // castaño arcilla
};
function catOf(c: string | null) {
  return (c && CAT[c]) || { label: c ?? "Event", color: "#4a5b52" };
}

function timeRange(o: Offer): string | null {
  if (o.all_day) return "All day";
  const opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", timeZone: "America/Cancun" };
  if (!o.starts_at) return null;
  const s = new Date(o.starts_at).toLocaleTimeString("en-US", opts);
  if (!o.ends_at) return s;
  return `${s} – ${new Date(o.ends_at).toLocaleTimeString("en-US", opts)}`;
}

/* ---------------------------------------------------------------- page */

function CalendarPage() {
  const { user } = useAuth();
  const [data, setData] = useState<CalendarResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);   // 'YYYY-MM'
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<CalendarResp>("/api/v1/calendar")
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? (e.problem.detail ?? e.message) : String(e));
      });
    return () => { cancelled = true; };
  }, [user]);

  // Months present in the data, e.g. ['2026-10','2026-11'].
  const months = useMemo(() => {
    const set = new Set<string>();
    data?.days.forEach((d) => set.add(d.date.slice(0, 7)));
    return [...set].sort();
  }, [data]);

  const activeMonth = month ?? months[0] ?? null;
  const visibleDays = useMemo(
    () => (data?.days ?? []).filter((d) => !activeMonth || d.date.startsWith(activeMonth)),
    [data, activeMonth],
  );
  const totalOffers = data?.days.reduce((n, d) => n + d.offers.length, 0) ?? 0;

  function monthLabel(ym: string): string {
    return new Date(ym + "-15T12:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  function stepMonth(dir: 1 | -1) {
    if (!activeMonth) return;
    const i = months.indexOf(activeMonth) + dir;
    if (i >= 0 && i < months.length) { setMonth(months[i]!); setOpenId(null); }
  }

  return (
    <AppShell>
      <section className="container max-w-3xl py-8 md:py-12">
        <header className="mb-5 text-center">
          <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">The Festival</p>
          <h1 className="font-serif text-4xl text-palm">Calendar</h1>
          {data && (
            <p className="mt-2 text-sm text-muted-foreground">
              October 28 – November 27, 2026 · {totalOffers} experiences
            </p>
          )}
        </header>

        {SHOW_PREVIEW_BANNER && (
          <div className="mb-6 rounded-xl border border-dashed border-palm/40 bg-sand px-4 py-3 text-center text-sm text-palm">
            <strong>Preview.</strong> This is a sample of the kind of experiences the festival will
            host — the confirmed schedule with real venues is coming soon.
            <span className="block text-xs text-muted-foreground">
              Vista previa: programa de muestra. El calendario confirmado con venues reales llegará pronto.
            </span>
          </div>
        )}

        {!data?.user.has_pass && (
          <Card className="mb-6 border-palm/30">
            <CardContent className="flex flex-col items-center gap-3 py-4 text-center md:flex-row md:justify-between md:text-left">
              <p className="text-sm text-palm">
                <strong>Unlock every detail.</strong> Pass holders see full descriptions, exact
                venues and times.
              </p>
              <Button asChild size="sm">
                <Link to={"/passes" as never}>Get your pass</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* month navigation — EventON "jump months" */}
        {months.length > 0 && activeMonth && (
          <div className="mb-6 flex items-center justify-between gap-3">
            <button
              onClick={() => stepMonth(-1)}
              disabled={months.indexOf(activeMonth) === 0}
              className="grid size-9 place-items-center rounded-full border text-palm transition-colors hover:bg-sand disabled:opacity-30"
              aria-label="Previous month"
            >‹</button>
            <div className="flex flex-1 items-center justify-center gap-2">
              {months.map((m) => (
                <button
                  key={m}
                  onClick={() => { setMonth(m); setOpenId(null); }}
                  className={
                    "rounded-full px-4 py-1.5 font-serif text-lg transition-colors " +
                    (m === activeMonth ? "bg-palm text-white" : "text-palm hover:bg-sand")
                  }
                >
                  {monthLabel(m)}
                </button>
              ))}
            </div>
            <button
              onClick={() => stepMonth(1)}
              disabled={months.indexOf(activeMonth) === months.length - 1}
              className="grid size-9 place-items-center rounded-full border text-palm transition-colors hover:bg-sand disabled:opacity-30"
              aria-label="Next month"
            >›</button>
          </div>
        )}

        {error && <p className="py-8 text-center text-sm text-destructive">{error}</p>}
        {!data && !error && <p className="py-16 text-center text-muted-foreground">Loading calendar…</p>}

        {/* day rows */}
        <div className="space-y-4">
          {visibleDays.map((day) => (
            <DayRow
              key={day.date}
              day={day}
              openId={openId}
              onToggle={(id) => setOpenId(openId === id ? null : id)}
              hasPass={Boolean(data?.user.has_pass)}
            />
          ))}
        </div>
      </section>
    </AppShell>
  );
}

/* ---------------------------------------------------------------- day row */

function DayRow({ day, openId, onToggle, hasPass }: {
  day: Day; openId: string | null; onToggle: (id: string) => void; hasPass: boolean;
}) {
  const d = new Date(day.date + "T12:00:00Z");
  return (
    <div className="flex gap-3">
      {/* date chip */}
      <div className="w-14 shrink-0 pt-1 text-center">
        <div className="font-serif text-2xl leading-none text-palm">{d.getUTCDate()}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}
        </div>
        {day.label && (
          <div className="mt-1 text-[9px] leading-tight text-muted-foreground/70">{day.label}</div>
        )}
      </div>

      {/* event bars */}
      <div className="min-w-0 flex-1 space-y-2">
        {day.offers.map((o) => (
          <EventBar key={o.id} offer={o} open={openId === o.id} onToggle={() => onToggle(o.id)} hasPass={hasPass} />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- event bar */

function EventBar({ offer: o, open, onToggle, hasPass }: {
  offer: Offer; open: boolean; onToggle: () => void; hasPass: boolean;
}) {
  const cat = catOf(o.category);
  const time = timeRange(o);

  return (
    <div
      className="overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md"
      style={{ borderLeft: `5px solid ${cat.color}` }}
    >
      {/* collapsed row — always visible, clickable */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            <span className="rounded-full px-2 py-0.5 font-medium text-white" style={{ background: cat.color }}>
              {cat.label}
            </span>
            {time && <span>{time.split(" – ")[0]}</span>}
            {o.venue?.name && <span>· {o.venue.name}</span>}
          </div>
          <div className="truncate font-serif text-[17px] leading-snug text-palm">{o.title}</div>
        </div>
        <div className="shrink-0 text-right">
          {o.is_locked
            ? <span className="text-base" title="Unlocks with a pass">🔒</span>
            : <span className={"inline-block text-muted-foreground transition-transform " + (open ? "rotate-180" : "")}>▾</span>}
          {o.spots_remaining != null && o.spots_remaining <= 30 && (
            <p className="mt-0.5 text-[10px] text-destructive">{o.spots_remaining} spots</p>
          )}
        </div>
      </button>

      {/* expanded lightbox */}
      {open && (
        <div className="border-t bg-sand/40 px-4 py-4">
          {o.image_url && (
            <img
              src={o.image_url}
              alt={o.title}
              className="mb-3 aspect-video w-full rounded-lg object-cover"
              loading="lazy"
            />
          )}

          <div className="mb-3 grid gap-2 text-sm md:grid-cols-2">
            {time && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Time</p>
                <p className="text-palm">{time}{o.is_locked && !o.ends_at ? "  ·  full range with a pass" : ""}</p>
              </div>
            )}
            {o.venue?.name && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Venue</p>
                <p className="text-palm">
                  {o.venue.name}
                  {o.venue.location_hint && <span className="text-muted-foreground"> · {o.venue.location_hint}</span>}
                </p>
              </div>
            )}
          </div>

          {o.is_locked ? (
            <>
              {o.teaser && <p className="text-sm italic text-muted-foreground">“{o.teaser}”</p>}
              <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-palm/25 bg-background px-3 py-2.5">
                <p className="text-xs text-palm">Full details, exact address and end time unlock with a pass.</p>
                <Button asChild size="sm">
                  <Link to={"/passes" as never}>Unlock</Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              {o.description && <p className="text-sm leading-relaxed">{o.description}</p>}
              {!o.description && o.teaser && <p className="text-sm text-muted-foreground">{o.teaser}</p>}
              {hasPass && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Show your pass QR at the door — find it in{" "}
                  <Link to={"/app/wallet" as never} className="underline underline-offset-2">My tickets</Link>.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
